import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, LogOut, Download, Sparkles, Mail, Users, Wallet, Inbox, CalendarDays, TrendingUp, Eye, Send, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import client, { API, formatApiError } from "../../lib/api";

const COLLECTIONS = {
  courses: {
    label: "Corsi",
    fields: [
      { key: "title", label: "Titolo", type: "text" },
      { key: "slug", label: "Slug (url)", type: "text" },
      { key: "subtitle", label: "Sottotitolo", type: "text" },
      { key: "description", label: "Descrizione", type: "textarea" },
      { key: "num_lessons", label: "N. lezioni", type: "number" },
      { key: "duration", label: "Durata", type: "text" },
      { key: "level", label: "Livello", type: "text" },
      { key: "target", label: "Target", type: "text" },
      { key: "price", label: "Prezzo (€)", type: "number" },
      { key: "status", label: "Stato", type: "select", options: ["disponibile", "in_arrivo"] },
      { key: "category", label: "Categoria", type: "text" },
      { key: "horeca", label: "Per Horeca", type: "checkbox" },
      { key: "order", label: "Ordine", type: "number" },
      { key: "stripe_lookup_key", label: "Stripe lookup key", type: "text" },
      { key: "lessons", label: "Programma (JSON: [{title, topics}])", type: "json" },
      { key: "learn", label: "Cosa imparerai (JSON: [\"...\"])", type: "json" },
    ],
    columns: ["title", "status", "price", "num_lessons"],
  },
  episodes: {
    label: "Episodi",
    fields: [
      { key: "number", label: "N. episodio", type: "number" },
      { key: "title", label: "Titolo", type: "text" },
      { key: "guest", label: "Ospite", type: "text" },
      { key: "series", label: "Serie", type: "select", options: ["I Classici", "Monologhi", "Cinema & Vino", "La Telefonata"] },
      { key: "duration", label: "Durata", type: "text" },
      { key: "date", label: "Data", type: "text" },
      { key: "youtube_url", label: "URL YouTube", type: "text" },
      { key: "spotify_url", label: "URL Spotify", type: "text" },
      { key: "description", label: "Descrizione", type: "textarea" },
    ],
    columns: ["number", "title", "series", "date"],
  },
  articles: {
    label: "Articoli",
    fields: [
      { key: "title", label: "Titolo", type: "text" },
      { key: "slug", label: "Slug (url)", type: "text" },
      { key: "category", label: "Categoria", type: "select", options: ["Vino", "Persone", "Territori", "Horeca", "Dietro le quinte"] },
      { key: "excerpt", label: "Estratto", type: "textarea" },
      { key: "content", label: "Contenuto (HTML: <p>…)", type: "textarea" },
      { key: "image", label: "URL immagine", type: "text" },
      { key: "reading_time", label: "Minuti lettura", type: "number" },
      { key: "published_at", label: "Data (YYYY-MM-DD)", type: "text" },
    ],
    columns: ["title", "category", "published_at"],
  },
  testimonials: {
    label: "Testimonianze",
    fields: [
      { key: "name", label: "Nome", type: "text" },
      { key: "role", label: "Professione", type: "text" },
      { key: "text", label: "Testo", type: "textarea" },
    ],
    columns: ["name", "role"],
  },
};

function Field({ f, value, onChange }) {
  const cls = "w-full rounded-lg px-3 py-2.5 bg-nero border border-panna/15 text-panna text-sm focus:outline-none focus:ring-2 focus:ring-oro/50";
  if (f.type === "textarea") return <textarea rows={4} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={cls} data-testid={`field-${f.key}`} />;
  if (f.type === "select") return (
    <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={cls} data-testid={`field-${f.key}`}>
      {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
  if (f.type === "checkbox") return (
    <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="h-5 w-5 accent-[#B0832F]" data-testid={`field-${f.key}`} />
  );
  if (f.type === "json") return (
    <textarea rows={4} value={typeof value === "string" ? value : JSON.stringify(value ?? [], null, 2)}
              onChange={(e) => onChange(e.target.value)} className={`${cls} font-mono text-xs`} data-testid={`field-${f.key}`} />
  );
  return <input type={f.type} value={value ?? ""} onChange={(e) => onChange(f.type === "number" ? Number(e.target.value) : e.target.value)} className={cls} data-testid={`field-${f.key}`} />;
}

function CrudTab({ name }) {
  const conf = COLLECTIONS[name];
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [aiTopic, setAiTopic] = useState("");
  const [aiLoading, setAiLoading] = useState(false);

  const load = () => client.get(`/admin/${name}`).then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, [name]);

  const generateDraft = async () => {
    if (!aiTopic.trim()) { toast.error("Scrivi prima l'argomento della storia"); return; }
    setAiLoading(true);
    try {
      const { data } = await client.post("/admin/ai/article-draft", {
        topic: aiTopic,
        category: editing.category || "Vino",
      });
      setEditing((ed) => ({ ...ed, title: data.title, excerpt: data.excerpt, content: data.content,
        slug: data.title ? data.title.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "") : ed.slug }));
      toast.success("Bozza generata — rivedila e salva");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAiLoading(false);
    }
  };

  const generateDescription = async () => {
    if (!editing?.title) { toast.error("Inserisci prima il titolo"); return; }
    setAiLoading(true);
    try {
      const { data } = await client.post("/admin/ai/describe", {
        kind: name === "courses" ? "corso" : "episodio",
        title: editing.title,
        notes: editing.subtitle || editing.guest || "",
      });
      setEditing((ed) => ({ ...ed, description: data.description }));
      toast.success("Descrizione generata");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAiLoading(false);
    }
  };

  const save = async () => {
    const data = { ...editing };
    for (const f of conf.fields) {
      if (f.type === "json" && typeof data[f.key] === "string") {
        try { data[f.key] = JSON.parse(data[f.key]); } catch { toast.error(`JSON non valido in "${f.label}"`); return; }
      }
    }
    try {
      if (data.id) {
        await client.put(`/admin/${name}/${data.id}`, data);
      } else {
        await client.post(`/admin/${name}`, data);
      }
      toast.success("Salvato");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare definitivamente?")) return;
    await client.delete(`/admin/${name}/${id}`);
    toast.success("Eliminato");
    load();
  };

  return (
    <div data-testid={`admin-tab-${name}`}>
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-serif text-xl font-semibold text-panna">{conf.label}</h2>
        <button onClick={() => setEditing({})} data-testid={`admin-${name}-new`}
                className="inline-flex items-center gap-2 rounded-full bg-bordeaux px-4 py-2 text-sm text-panna hover:bg-bordeaux-dark transition-colors">
          <Plus size={15} /> Nuovo
        </button>
      </div>
      {editing && (
        <div className="mb-8 rounded-xl border border-oro/30 bg-nero p-6">
          <h3 className="text-panna font-medium mb-5">{editing.id ? "Modifica" : "Nuovo"} — {conf.label}</h3>
          {name === "articles" && (
            <div className="mb-6 rounded-xl border border-oliva/40 bg-oliva/10 p-5">
              <p className="text-xs uppercase tracking-[0.2em] text-oliva mb-3">Bozza con AI (Claude)</p>
              <div className="flex gap-3">
                <input value={aiTopic} onChange={(e) => setAiTopic(e.target.value)}
                       placeholder="Argomento della storia (es. il Nebbiolo delle Langhe)"
                       data-testid="ai-topic-input"
                       className="flex-1 rounded-lg px-3 py-2.5 bg-nero border border-panna/15 text-panna text-sm focus:outline-none focus:ring-2 focus:ring-oro/50" />
                <button type="button" onClick={generateDraft} disabled={aiLoading} data-testid="ai-generate-draft"
                        className="inline-flex items-center gap-2 rounded-full bg-oliva px-5 py-2.5 text-sm text-panna hover:opacity-90 transition-opacity disabled:opacity-50 whitespace-nowrap">
                  <Sparkles size={14} /> {aiLoading ? "Genero…" : "Genera bozza"}
                </button>
              </div>
              <p className="text-[0.7rem] text-panna/40 mt-2">Compila titolo, estratto, contenuto e slug. La bozza va sempre riletta prima di salvare.</p>
            </div>
          )}
          {(name === "courses" || name === "episodes") && (
            <div className="mb-6">
              <button type="button" onClick={generateDescription} disabled={aiLoading} data-testid="ai-generate-description"
                      className="inline-flex items-center gap-2 rounded-full border border-oliva/50 px-5 py-2.5 text-sm text-oliva hover:bg-oliva/10 transition-colors disabled:opacity-50">
                <Sparkles size={14} /> {aiLoading ? "Genero…" : "Genera descrizione con AI"}
              </button>
              <p className="text-[0.7rem] text-panna/40 mt-2">Usa il titolo (e sottotitolo/ospite) per scrivere la descrizione con Claude.</p>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {conf.fields.map((f) => (
              <div key={f.key} className={f.type === "textarea" || f.type === "json" ? "sm:col-span-2" : ""}>
                <label className="block text-xs text-panna/50 mb-1.5">{f.label}</label>
                <Field f={f} value={editing[f.key]} onChange={(v) => setEditing({ ...editing, [f.key]: v })} />
              </div>
            ))}
          </div>
          <div className="flex gap-3 mt-6">
            <button onClick={save} data-testid="admin-save" className="rounded-full bg-bordeaux px-6 py-2.5 text-sm text-panna hover:bg-bordeaux-dark transition-colors">Salva</button>
            <button onClick={() => setEditing(null)} className="rounded-full border border-panna/25 px-6 py-2.5 text-sm text-panna/70 hover:border-oro/50 transition-colors">Annulla</button>
          </div>
        </div>
      )}
      <div className="overflow-x-auto rounded-xl border border-panna/10">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-nero-light text-panna/60 text-left">
              {conf.columns.map((c) => <th key={c} className="px-4 py-3 font-medium">{c}</th>)}
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} className="border-t border-panna/5 text-panna/80">
                {conf.columns.map((c) => <td key={c} className="px-4 py-3 max-w-[240px] truncate">{String(it[c] ?? "")}</td>)}
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => setEditing(it)} data-testid={`edit-${it.id}`} className="text-oro hover:text-oro-light mr-3" aria-label="Modifica"><Pencil size={15} /></button>
                  <button onClick={() => remove(it.id)} data-testid={`delete-${it.id}`} className="text-bordeaux hover:text-oro-light" aria-label="Elimina"><Trash2 size={15} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && <p className="text-panna/40 text-sm p-6 text-center">Nessun elemento.</p>}
      </div>
    </div>
  );
}

function ListTab({ endpoint, columns, testId, extra }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    client.get(`/admin/${endpoint}`).then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [endpoint]);
  return (
    <div data-testid={testId}>
      {extra}
      <div className="overflow-x-auto rounded-xl border border-panna/10 mt-4">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-nero-light text-panna/60 text-left">
              {columns.map(([k, l]) => <th key={k} className="px-4 py-3 font-medium">{l}</th>)}
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={it.id || i} className="border-t border-panna/5 text-panna/80">
                {columns.map(([k]) => <td key={k} className="px-4 py-3 max-w-[240px] truncate">{String(it[k] ?? "")}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && <p className="text-panna/40 text-sm p-6 text-center">Nessun elemento.</p>}
      </div>
    </div>
  );
}

function SettingsTab() {
  const [s, setS] = useState(null);
  useEffect(() => { client.get("/admin/settings").then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s) return null;
  const social = s.social || {};
  const save = async () => {
    await client.put("/admin/settings", s);
    toast.success("Impostazioni salvate");
  };
  const cls = "w-full rounded-lg px-3 py-2.5 bg-nero border border-panna/15 text-panna text-sm focus:outline-none focus:ring-2 focus:ring-oro/50";
  return (
    <div data-testid="admin-tab-settings" className="max-w-2xl space-y-5">
      <h2 className="font-serif text-xl font-semibold text-panna">Impostazioni</h2>
      {[["amazon_url", "Link Amazon del libro"], ["ga4_id", "ID Google Analytics 4"], ["meta_pixel_id", "ID Meta Pixel"], ["brevo_api_key", "API key Brevo"]].map(([k, l]) => (
        <div key={k}>
          <label className="block text-xs text-panna/50 mb-1.5">{l}</label>
          <input value={s[k] || ""} onChange={(e) => setS({ ...s, [k]: e.target.value })} className={cls} data-testid={`setting-${k}`} />
        </div>
      ))}
      <p className="text-xs text-panna/50 uppercase tracking-widest pt-2">Social</p>
      {Object.keys({ instagram: 1, youtube: 1, tiktok: 1, facebook: 1, spotify: 1 }).map((k) => (
        <div key={k}>
          <label className="block text-xs text-panna/50 mb-1.5 capitalize">{k}</label>
          <input value={social[k] || ""} onChange={(e) => setS({ ...s, social: { ...social, [k]: e.target.value } })} className={cls} data-testid={`setting-social-${k}`} />
        </div>
      ))}
      <button onClick={save} data-testid="settings-save" className="rounded-full bg-bordeaux px-8 py-3 text-sm text-panna hover:bg-bordeaux-dark transition-colors">Salva impostazioni</button>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, testId }) {
  return (
    <div data-testid={testId} className="rounded-2xl border border-panna/10 bg-nero-light/40 p-5">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider text-panna/50">{label}</span>
        <Icon size={18} className="text-oro" />
      </div>
      <div className="mt-3 font-serif text-3xl font-bold text-panna">{value}</div>
      {sub && <div className="mt-1 text-xs text-panna/45">{sub}</div>}
    </div>
  );
}

function OverviewTab() {
  const [s, setS] = useState(null);
  useEffect(() => { client.get("/admin/stats").then((r) => setS(r.data)).catch((e) => toast.error(formatApiError(e))); }, []);
  if (!s) return <div className="py-16 text-center text-panna/40 text-sm">Carico la panoramica…</div>;

  const srcLabels = { home: "Home", "consigliere-vino": "Consigliere AI", "libro-bonus": "Bonus libro", "lp-libro": "Landing /lp/libro", "lead-magnet": "Lead magnet", test: "Test" };
  const sources = Object.entries(s.by_source || {});
  const maxSrc = Math.max(1, ...sources.map(([, v]) => v));
  const days = Object.entries(s.by_day || {}).slice(-14);
  const maxDay = Math.max(1, ...days.map(([, v]) => v));

  return (
    <div data-testid="admin-tab-overview" className="space-y-8">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testId="stat-visits" icon={Eye} label="Visite sito" value={(s.visitors_total ?? 0).toLocaleString("it-IT")}
                  sub={`${s.visitors_week ?? 0} negli ultimi 7 gg · ${s.visitors_today ?? 0} oggi`} />
        <StatCard testId="stat-leads" icon={Users} label="Lead totali" value={s.leads_total}
                  sub={`${s.leads_week} negli ultimi 7 gg · ${s.leads_today} oggi`} />
        <StatCard testId="stat-counter" icon={TrendingUp} label="Contatore pubblico" value={s.leads_public_count}
                  sub={`base ${s.leads_public_count - s.leads_total} + ${s.leads_total} reali`} />
        <StatCard testId="stat-orders" icon={Wallet} label="Ordini pagati" value={s.orders_paid}
                  sub={`${s.orders_pending} in sospeso · incasso €${(s.revenue_cents / 100).toFixed(0)}`} />
        <StatCard testId="stat-other" icon={Inbox} label="Waitlist · Bonus" value={`${s.waitlist} · ${s.bonus}`}
                  sub={`${s.contacts} messaggi contatti`} />
      </div>

      {s.leads_not_sent > 0 && (
        <div data-testid="stat-alert" className="rounded-xl border border-oro/40 bg-oro/10 px-5 py-3 text-sm text-oro">
          ⚠️ {s.leads_not_sent} lead non hanno ancora ricevuto la guida — usa "Reinvia guida ai sospesi" nel tab Lead.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-panna/10 bg-nero-light/40 p-6">
          <h3 className="font-serif text-lg font-semibold flex items-center gap-2"><Sparkles size={16} className="text-oro" /> Da dove arrivano i lead</h3>
          <div className="mt-5 space-y-3">
            {sources.map(([k, v]) => (
              <div key={k} data-testid={`src-${k}`}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-panna/75">{srcLabels[k] || k}</span><span className="text-oro font-semibold">{v}</span>
                </div>
                <div className="h-2 rounded-full bg-panna/10 overflow-hidden">
                  <div className="h-full bg-oro rounded-full" style={{ width: `${(v / maxSrc) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-panna/10 bg-nero-light/40 p-6">
          <h3 className="font-serif text-lg font-semibold flex items-center gap-2"><CalendarDays size={16} className="text-oro" /> Lead per giorno</h3>
          <div className="mt-6 flex items-end gap-1.5 h-40">
            {days.map(([d, v]) => (
              <div key={d} className="flex-1 flex flex-col items-center gap-1.5 group">
                <span className="text-[10px] text-oro opacity-0 group-hover:opacity-100">{v}</span>
                <div className="w-full bg-bordeaux rounded-t transition-all" style={{ height: `${(v / maxDay) * 100}%`, minHeight: v ? "6px" : "2px" }} />
                <span className="text-[9px] text-panna/40">{d.slice(8)}/{d.slice(5, 7)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-panna/10 bg-nero-light/40 p-6">
        <h3 className="font-serif text-lg font-semibold">Ultimi lead</h3>
        <div className="mt-4 divide-y divide-panna/10">
          {s.recent_leads.map((l) => (
            <div key={l.id} className="flex items-center justify-between py-2.5 text-sm">
              <span className="text-panna/80">{l.name || "—"} · <span className="text-panna/50">{l.email}</span></span>
              <span className="text-panna/40 text-xs">{srcLabels[l.source] || l.source} · {(l.created_at || "").slice(0, 10)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const BOOK_TEMPLATE = `Ciao,

spero che la guida gratuita ti sia stata utile e che tu abbia già evitato qualche errore di troppo! 🍷

Se ti è venuta voglia di andare più a fondo nel mondo del vino, ho scritto un libro che fa proprio al caso tuo: "Dentro il Calice — Italia".

È un viaggio tra le cantine e le storie del vino italiano, regione per regione, raccontato in modo semplice e senza snobismi — proprio come piace a me.

Lo trovi qui:
{AMAZON}

Grazie di cuore per la fiducia.`;

function BroadcastTab() {
  const [subject, setSubject] = useState('Un regalo per te: il mio libro "Dentro il Calice"');
  const [message, setMessage] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [count, setCount] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const baseUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    client.get("/settings/public")
      .then((r) => setMessage(BOOK_TEMPLATE.replace("{AMAZON}", r.data.amazon_url || "https://www.amazon.it")))
      .catch(() => setMessage(BOOK_TEMPLATE.replace("{AMAZON}", "https://www.amazon.it")));
    client.get("/admin/stats").then((r) => setCount(r.data.leads_total)).catch(() => {});
  }, []);

  const disabled = !subject.trim() || !message.trim() || busy;

  const sendTest = async () => {
    if (!testEmail.trim()) return toast.error("Scrivi la tua email per ricevere la prova");
    setBusy(true);
    try {
      await client.post("/admin/broadcast/test", { subject, message, email: testEmail.trim(), base_url: baseUrl });
      toast.success(`Email di prova inviata a ${testEmail.trim()} — controlla la casella (anche lo spam)`);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const sendAll = async () => {
    setBusy(true);
    try {
      const r = await client.post("/admin/broadcast", { subject, message, base_url: baseUrl });
      toast.success(`Email inviata a ${r.data.sent} contatti${r.data.failed ? `, ${r.data.failed} non riuscite` : ""}!`);
      setConfirm(false);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const inputCls = "w-full rounded-lg px-3 py-2.5 bg-nero border border-panna/15 text-panna text-sm focus:outline-none focus:ring-2 focus:ring-oro/50";

  const syncBrevo = async () => {
    setBusy(true);
    try {
      const r = await client.post("/admin/brevo-sync");
      toast.success(`${r.data.synced} contatti sincronizzati nella lista Brevo "Lead sito"`);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  return (
    <div data-testid="admin-tab-broadcast" className="max-w-3xl">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-serif text-2xl font-bold text-panna">Email a tutti i contatti</h2>
          <p className="text-panna/60 text-sm mt-1">
            Scrivi un messaggio e invialo a tutti i tuoi iscritti. Prima manda una <strong>prova a te stesso</strong>, poi spedisci a tutti.
          </p>
        </div>
        <button data-testid="brevo-sync-btn" onClick={syncBrevo} disabled={busy}
                className="inline-flex items-center gap-2 rounded-full border border-panna/20 px-4 py-2 text-xs text-panna/70 hover:text-panna hover:border-oro/40 transition-colors disabled:opacity-40">
          <Users size={14} /> Sincronizza contatti su Brevo
        </button>
      </div>

      <div className="space-y-5 rounded-2xl border border-panna/10 bg-nero-light/40 p-5 sm:p-6">
        <div>
          <label className="block text-xs uppercase tracking-wider text-panna/60 mb-1.5">Oggetto</label>
          <input data-testid="broadcast-subject" value={subject} onChange={(e) => setSubject(e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wider text-panna/60 mb-1.5">Testo dell'email</label>
          <textarea data-testid="broadcast-message" rows={14} value={message} onChange={(e) => setMessage(e.target.value)}
                    className={`${inputCls} leading-relaxed`} />
          <p className="text-panna/40 text-xs mt-1.5">
            Suggerimento: scrivi come parleresti a un amico. I link (es. Amazon) diventano cliccabili in automatico. Lascia una riga vuota per andare a capo tra i paragrafi.
          </p>
        </div>

        <div className="rounded-xl border border-oro/20 bg-oro/5 p-4">
          <label className="block text-xs uppercase tracking-wider text-oro/80 mb-1.5">1. Prova prima (consigliato)</label>
          <div className="flex flex-col sm:flex-row gap-2">
            <input data-testid="broadcast-test-email" type="email" placeholder="la-tua-email@esempio.it"
                   value={testEmail} onChange={(e) => setTestEmail(e.target.value)} className={`${inputCls} flex-1`} />
            <button data-testid="broadcast-send-test" onClick={sendTest} disabled={disabled}
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-oro/50 px-5 py-2.5 text-sm text-oro hover:bg-oro/10 transition-colors disabled:opacity-40">
              <Mail size={15} /> Invia prova a me
            </button>
          </div>
        </div>

        {!confirm ? (
          <div>
            <label className="block text-xs uppercase tracking-wider text-panna/60 mb-1.5">2. Invia a tutti</label>
            <button data-testid="broadcast-send-all" onClick={() => setConfirm(true)} disabled={disabled}
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-bordeaux px-6 py-3 text-sm font-semibold text-panna hover:bg-bordeaux/85 transition-colors disabled:opacity-40">
              <Send size={16} /> Invia a tutti i contatti{count != null ? ` (${count})` : ""}
            </button>
          </div>
        ) : (
          <div data-testid="broadcast-confirm" className="rounded-xl border border-bordeaux/40 bg-bordeaux/10 p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle size={18} className="text-oro mt-0.5 shrink-0" />
              <div>
                <p className="text-panna text-sm font-semibold">Confermi l'invio a tutti i contatti{count != null ? ` (${count})` : ""}?</p>
                <p className="text-panna/60 text-xs mt-1">L'email partirà subito. Assicurati di aver controllato la prova.</p>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button data-testid="broadcast-confirm-yes" onClick={sendAll} disabled={busy}
                      className="inline-flex items-center gap-2 rounded-full bg-bordeaux px-5 py-2.5 text-sm font-semibold text-panna hover:bg-bordeaux/85 transition-colors disabled:opacity-50">
                <Send size={15} /> {busy ? "Invio in corso…" : "Sì, invia a tutti"}
              </button>
              <button data-testid="broadcast-confirm-cancel" onClick={() => setConfirm(false)} disabled={busy}
                      className="rounded-full border border-panna/20 px-5 py-2.5 text-sm text-panna/70 hover:text-panna transition-colors">
                Annulla
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const TABS = [
  ["overview", "Panoramica"],
  ["courses", "Corsi"], ["episodes", "Episodi"], ["articles", "Articoli"], ["testimonials", "Testimonianze"],
  ["leads", "Lead"], ["contacts", "Messaggi"], ["waitlist", "Liste d'attesa"], ["bonus", "Bonus webinar"],
  ["orders", "Ordini"], ["broadcast", "Email a tutti"], ["settings", "Impostazioni"],
];

export default function AdminDashboard() {
  const [tab, setTab] = useState("overview");
  const [auth, setAuth] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    client.get("/auth/me").then(() => setAuth(true)).catch(() => navigate("/admin/login"));
  }, [navigate]);

  const logout = () => {
    localStorage.removeItem("dic_admin_token");
    navigate("/admin/login");
  };

  if (!auth) return <div className="min-h-screen bg-nero" />;

  const exportLeads = () => {
    const token = localStorage.getItem("dic_admin_token");
    fetch(`${API}/admin/leads/export`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((b) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(b);
        a.download = "lead-dentro-il-calice.csv";
        a.click();
      });
  };

  const resendPending = async () => {
    if (!window.confirm("Reinviare la guida PDF a tutti i lead che non l'hanno ancora ricevuta?")) return;
    try {
      const { data } = await client.post("/admin/leads/resend-pending");
      toast.success(`Inviate ${data.sent} email su ${data.total} in sospeso${data.failed ? ` — ${data.failed} non riuscite` : ""}.`);
      setTimeout(() => window.location.reload(), 1400);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-nero text-panna">
      <header className="border-b border-panna/10 bg-nero-light">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/assets/logo-dark.png" alt="Dentro il Calice" className="h-10 w-auto" />
            <span className="text-xs uppercase tracking-[0.25em] text-oro">Admin</span>
          </div>
          <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 text-sm text-panna/60 hover:text-oro transition-colors">
            <LogOut size={15} /> Esci
          </button>
        </div>
      </header>
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-8">
        <div className="flex gap-2 overflow-x-auto pb-4 mb-6" data-testid="admin-tabs">
          {TABS.map(([key, label]) => (
            <button key={key} data-testid={`tab-${key}`} onClick={() => setTab(key)}
                    className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                      tab === key ? "bg-bordeaux text-panna" : "text-panna/60 hover:text-oro border border-panna/10"
                    }`}>
              {label}
            </button>
          ))}
        </div>
        {tab === "overview" && <OverviewTab />}
        {COLLECTIONS[tab] && <CrudTab name={tab} />}
        {tab === "leads" && (
          <ListTab endpoint="leads" testId="admin-tab-leads"
                   columns={[["name", "Nome"], ["email", "Email"], ["source", "Fonte"], ["email_status", "Email"], ["created_at", "Data"]]}
                   extra={
                     <div className="flex flex-wrap gap-2">
                       <button onClick={resendPending} data-testid="leads-resend-pending"
                               className="inline-flex items-center gap-2 rounded-full border border-oro/50 px-4 py-2 text-sm text-oro hover:bg-oro/10 transition-colors">
                         <Mail size={15} /> Reinvia guida ai sospesi
                       </button>
                       <button onClick={exportLeads} data-testid="leads-export"
                               className="inline-flex items-center gap-2 rounded-full border border-oro/50 px-4 py-2 text-sm text-oro hover:bg-oro/10 transition-colors">
                         <Download size={15} /> Esporta CSV
                       </button>
                     </div>
                   } />
        )}
        {tab === "contacts" && (
          <ListTab endpoint="contacts" testId="admin-tab-contacts"
                   columns={[["name", "Nome"], ["email", "Email"], ["reason", "Motivo"], ["message", "Messaggio"], ["created_at", "Data"]]} />
        )}
        {tab === "waitlist" && (
          <ListTab endpoint="waitlist" testId="admin-tab-waitlist"
                   columns={[["email", "Email"], ["course_slug", "Corso"], ["created_at", "Data"]]} />
        )}
        {tab === "bonus" && (
          <ListTab endpoint="bonus" testId="admin-tab-bonus"
                   columns={[["email", "Email"], ["amazon_order_id", "Ordine Amazon"], ["created_at", "Data"]]} />
        )}
        {tab === "orders" && (
          <ListTab endpoint="orders" testId="admin-tab-orders"
                   columns={[["session_id", "Sessione"], ["lookup_key", "Corso"], ["amount", "Importo (cent.)"], ["currency", "Valuta"], ["payment_status", "Stato"], ["customer_email", "Email"], ["created_at", "Data"]]} />
        )}
        {tab === "broadcast" && <BroadcastTab />}
        {tab === "settings" && <SettingsTab />}
      </div>
    </div>
  );
}
